import { useState, useEffect, useCallback, forwardRef, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';
import MeasurementIconSelectorModal, { MeasurementIconType } from './MeasurementIconSelectorModal';

type LibraryMeasurementParam = {
  id: number;
  name: string;
  icon_url?: string;
  reference_id?: string;
  image_link?: string;
};

type AddMeasurementModalProps = {
  atomicOutfitId: number | null;
  existingParamIds: number[];
  onMeasurementAdded?: () => void;
};

const AddMeasurementModal = forwardRef<ModalMethods, AddMeasurementModalProps>(
  ({ atomicOutfitId, existingParamIds, onMeasurementAdded }, ref) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [libraryParams, setLibraryParams] = useState<LibraryMeasurementParam[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [selectedIcon, setSelectedIcon] = useState<MeasurementIconType | null>(null);
    const iconSelectorModalRef = useRef<ModalMethods>(null);

    const fetchLibraryParams = useCallback(async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsLoading(true);

      try {
        const response = await api.getRequest(
          `new_measurement/v2/${boutique_id}/boutique_measurements/all`
        );

        if (response.status && response.data) {
          const rawData = Array.isArray(response.data) ? response.data : [];
          const params: LibraryMeasurementParam[] = rawData.map(
            (item: { id: number; name: string; icon_url?: string; image_ref_id?: string }) => ({
              id: item.id,
              name: item.name || '',
              icon_url: item.icon_url,
              reference_id: item.image_ref_id,
              image_link: item.image_ref_id,
            })
          );

          setLibraryParams(params);
        } else {
          setLibraryParams([]);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'fetch-measurement-library-error');
        }
        setLibraryParams([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    useEffect(() => {
      void fetchLibraryParams();
    }, [fetchLibraryParams]);

    const filteredParams = libraryParams.filter((param) => {
      const matchesSearch = _isEmpty(searchQuery.trim())
        ? true
        : (param.name || '').toLowerCase().includes(searchQuery.trim().toLowerCase());
      const notAlreadyAdded = !existingParamIds.includes(param.id);

      return matchesSearch && notAlreadyAdded;
    });

    const isQueryInLibrary = libraryParams.some(
      (param) => param.name?.toLowerCase() === searchQuery.trim().toLowerCase()
    );
    const showCreateButton =
      !_isEmpty(searchQuery.trim()) && (!isQueryInLibrary || filteredParams.length === 0);

    const handleSelectFromLibrary = async (param: LibraryMeasurementParam) => {
      if (!atomicOutfitId) {
        toasts('error', 'No piece selected', 'add-measurement-no-piece');

        return;
      }

      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsCreating(true);

      try {
        const imageLink = param.image_link || param.reference_id || String(param.id);
        const response = await api.postRequest(
          `new_measurement/v2/${boutique_id}/boutique_measurements?atomic_outfit_id=${atomicOutfitId}`,
          {
            name: param.name,
            id_custom: false,
            image_link: imageLink,
          }
        );

        if (response.status) {
          toasts('success', 'Measurement added successfully', 'measurement-add-success');

          if (onMeasurementAdded) {
            onMeasurementAdded();
          }

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts('error', response.message || 'Failed to add measurement', 'measurement-add-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'measurement-add-error');
        }
      } finally {
        setIsCreating(false);
      }
    };

    const handleCreateNew = async () => {
      if (!atomicOutfitId) {
        toasts('error', 'No piece selected', 'add-measurement-no-piece');

        return;
      }

      const trimmedName = searchQuery.trim();

      if (_isEmpty(trimmedName)) {
        toasts('error', 'Please enter a measurement name', 'measurement-name-required');

        return;
      }

      if (!selectedIcon?.image_link) {
        toasts('error', 'Please select or upload an icon for the measurement', 'icon-required');

        return;
      }

      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsCreating(true);

      try {
        const response = await api.postRequest(
          `new_measurement/v2/${boutique_id}/boutique_measurements?atomic_outfit_id=${atomicOutfitId}`,
          {
            name: trimmedName,
            id_custom: true,
            image_link: selectedIcon.image_link,
          }
        );

        if (response.status) {
          toasts('success', 'Measurement created successfully', 'measurement-create-success');

          if (onMeasurementAdded) {
            onMeasurementAdded();
          }

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts(
            'error',
            response.message || 'Failed to create measurement',
            'measurement-create-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'measurement-create-error');
        }
      } finally {
        setIsCreating(false);
      }
    };

    const handleOpenIconSelector = () => {
      iconSelectorModalRef.current?.show();
    };

    const handleIconSelect = (icon: MeasurementIconType) => {
      setSelectedIcon(icon);
      iconSelectorModalRef.current?.hide();
    };

    return (
      <>
        <Modal
          ref={ref}
          size="xl"
          title="Select existing or add new measurement"
          showFooter={false}
          showCloseIcon
        >
          <div className="flex flex-col h-full min-h-[400px]">
            <div className="mb-1">
              <Text className="font-inter text-sm text-gray-600 mb-4 mt-1 block" color="black">
                Select any measurement from the library to add to this piece, or create a custom one
                with your own icon.
              </Text>
              <div className="relative flex items-center w-full">
                <input
                  type="text"
                  placeholder="Search or type a new measurement"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
                  autoFocus
                />
              </div>
            </div>

            {showCreateButton && (
              <div className="mb-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleOpenIconSelector}
                  className="flex items-center gap-2 px-4 py-2.5 bg-transparent border border-gray-300 rounded-lg cursor-pointer transition-all hover:bg-gray-50 hover:border-blue-500"
                >
                  {selectedIcon?.icon_url ? (
                    <img
                      src={selectedIcon.icon_url}
                      alt={selectedIcon.name || 'Icon'}
                      className="w-8 h-8 object-contain rounded"
                    />
                  ) : (
                    <span className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded text-gray-500 text-sm">
                      +
                    </span>
                  )}
                  <Text size="small" color="black">
                    {selectedIcon ? 'Change icon' : 'Select icon'}
                  </Text>
                </button>
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <Loader />
              </div>
            ) : (
              <>
                {filteredParams.length > 0 ? (
                  <div className="flex flex-col gap-2 py-2 max-h-[300px] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:hover:bg-gray-500">
                    {filteredParams.map((param) => (
                      <div
                        key={param.id}
                        onClick={() => handleSelectFromLibrary(param)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all hover:bg-gray-50 border border-transparent hover:border-gray-200"
                      >
                        {param.icon_url && (
                          <img
                            src={param.icon_url}
                            alt={param.name}
                            className="w-8 h-8 object-contain rounded"
                          />
                        )}
                        <Text size="medium" color="black" className="font-inter">
                          {param.name}
                        </Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-center items-center h-[200px] p-5">
                    <Text color="gray" size="small" className="font-inter">
                      {searchQuery
                        ? 'No measurements found matching your search'
                        : 'No measurements available in library'}
                    </Text>
                  </div>
                )}
              </>
            )}

            {showCreateButton && (
              <div className="mt-auto pb-5 border-t border-gray-300 pt-4">
                <button
                  type="button"
                  onClick={handleCreateNew}
                  disabled={isCreating || !selectedIcon?.image_link}
                  className="w-full px-4 py-4 bg-blue-600 text-white rounded-lg font-semibold transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <Loader />
                      <Text size="small" color="white">
                        Creating...
                      </Text>
                    </>
                  ) : (
                    <Text
                      color="white"
                      className="font-inter font-medium text-sm leading-none tracking-normal"
                    >
                      + Create &quot;{searchQuery.trim()}&quot; as new Measurement
                    </Text>
                  )}
                </button>
              </div>
            )}
          </div>
        </Modal>

        <MeasurementIconSelectorModal
          ref={iconSelectorModalRef}
          onIconSelect={handleIconSelect}
          selectedIconId={selectedIcon?.id}
        />
      </>
    );
  }
);

AddMeasurementModal.displayName = 'AddMeasurementModal';

export default AddMeasurementModal;
